// src/components/StudentDashboard.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import ProfileHeader from './ProfileHeader';
import {
  getStudentVideoState,
  getVideoUrl,
  getVideoTitle,
  isInReviewVideo,
  isReviewedVideo,
  isUploadFailed,
} from '../utils/videoWorkflow';
import { buildMySkillsPath } from '../utils/mySkillsPath';
import { buildStudentLessonPath } from '../utils/studentLessonPath';
import { getProgressionRecommendationDisplay } from '../utils/progressionPresentation';
import { buildStudentProgressSummary } from '../utils/progressTracking';
import { normalizeSportName } from '../utils/drillTemplates';
import { loadVisibleLessonsForStudent } from '../utils/studentLessonAccess';
import {
  getBookingsRequiringFollowUp,
  getRequesterBookingRequests,
  getRequesterBookings,
  getUpcomingBookingsForUser,
} from '../utils/booking';

function formatThemeList(items = [], limit = 2) {
  const labels = (items || []).slice(0, limit).map((item) => item?.label).filter(Boolean);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels[0]}, ${labels[1]}, and ${labels[2]}`;
}

function getAvailableSkillSports(videos = []) {
  const sports = Array.from(
    new Set(
      (videos || [])
        .map((video) => normalizeSportName(video?.sport || ''))
        .filter((sport) => sport === 'skiing' || sport === 'snowboarding')
    )
  );

  if (sports.length === 0) {
    return ['skiing', 'snowboarding'];
  }

  return sports;
}

function StudentDashboard() {
  const { currentUser, userData } = useAuth();
  const focusedVideoId = new URLSearchParams(window.location.search).get('video');
  const [videos, setVideos] = useState([]);
  const [feedbackMap, setFeedbackMap] = useState({});
  const [lessonInstances, setLessonInstances] = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [lessonClaimMessage, setLessonClaimMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const videoRefs = useRef({});
  const cardRefs = useRef({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVideosAndFeedback = async () => {
      if (!currentUser?.uid) return;

      setLoading(true);
      try {
        const baseQuery = query(
          collection(db, 'videos'),
          where('userId', '==', currentUser.uid)
        );

        const snapshot = await getDocs(baseQuery);
        const [
          { lessons: visibleLessons, claimedCount },
          nextBookingRequests,
          nextBookings,
          nextUpcomingBookings,
        ] = await Promise.all([
          loadVisibleLessonsForStudent(
            db,
            currentUser,
            { studentName: userData?.name || '' }
          ),
          getRequesterBookingRequests(db, currentUser.uid),
          getRequesterBookings(db, currentUser.uid),
          getUpcomingBookingsForUser(db, {
            currentUserId: currentUser.uid,
            role: 'student',
          }),
        ]);

        // Pass 72 (§20) — a READ path must not mutate video chronology. The legacy createdAt backfill write was
        // removed (createdAt is stamped once at create); records missing it simply sort last.
        const videoList = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));

        const sortedVideos = videoList.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        setVideos(sortedVideos);
        setLessonInstances(visibleLessons);
        setBookingRequests(nextBookingRequests || []);
        setBookings(
          (nextBookings || []).map((booking) => {
            const matchingUpcoming = (nextUpcomingBookings || []).find(
              (upcoming) => upcoming.bookingId === booking.bookingId
            );
            return matchingUpcoming
              ? { ...booking, status: 'upcoming' }
              : booking;
          })
        );
        setLessonClaimMessage(
          claimedCount > 0
            ? `We linked ${claimedCount} lesson${claimedCount === 1 ? '' : 's'} created for your email to your account.`
            : ''
        );

        const fbMap = {};
        await Promise.all(
          sortedVideos.map(async (video) => {
            if (video.status === 'reviewed') {
              const fbSnap = await getDocs(
                collection(db, 'videos', video.id, 'timestampedFeedback')
              );
              fbMap[video.id] = (fbSnap?.docs || [])
                .map((doc) => doc.data())
                .sort((a, b) => a.timestamp - b.timestamp);
            }
          })
        );

        setFeedbackMap(fbMap);
      } catch (err) {
        console.error('❌ Error loading student dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchVideosAndFeedback();
  }, [currentUser, userData?.name]);

  useEffect(() => {
    if (!focusedVideoId || loading) return;
    const card = cardRefs.current[focusedVideoId];
    if (card?.scrollIntoView) {
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [focusedVideoId, loading]);

  const filteredVideos = videos.filter((video) =>
    filter ? getVideoTitle(video).toLowerCase().includes(filter.toLowerCase()) : true
  );

  const totalVideos = videos.length;
  const reviewedVideos = videos.filter(isReviewedVideo).length;
  const failedUploads = videos.filter(isUploadFailed).length;
  const inReviewVideos = videos.filter(isInReviewVideo).length;
  const awaitingInstructorVideos = videos.filter(
    (video) => !isUploadFailed(video) && !isReviewedVideo(video) && !isInReviewVideo(video)
  ).length;
  const progressSummary = buildStudentProgressSummary(videos, { feedbackMap, lessonInstances });
  const progressionDisplay = getProgressionRecommendationDisplay(progressSummary);
  const plannedLessons = lessonInstances.filter((lesson) => lesson.status !== 'completed');
  const completedLessons = lessonInstances.filter((lesson) => lesson.status === 'completed');
  const upcomingBookings = bookings.filter((booking) => booking.status === 'upcoming' || booking.status === 'confirmed');
  const followUpBookings = getBookingsRequiringFollowUp(bookings) || [];
  const feedbackReadyBookings = bookings.filter((booking) => booking.feedbackId || booking.aiReviewId || booking.videoId);
  const isSnowboardProgress = progressSummary.dominantSport === 'snowboarding';
  const availableSkillSports = getAvailableSkillSports(videos);
  const skillCardSummaries = useMemo(
    () =>
      availableSkillSports.map((sport) => {
        const sportVideos = videos.filter((video) => normalizeSportName(video?.sport || '') === sport);
        const summary = buildStudentProgressSummary(sportVideos, { feedbackMap, lessonInstances, sport });
        const levelLabel = summary.currentLevel ? summary.currentLevel.replace(/_/g, ' ') : 'Not started';
        const workingOn = summary.currentSkill?.name || summary.nextSkill?.name || 'Building foundations';

        return {
          sport,
          levelLabel,
          workingOn,
        };
      }),
    [availableSkillSports, feedbackMap, lessonInstances, videos]
  );
  const snowboardRecurringThemeText = formatThemeList(progressSummary.recurringIssues);
  const snowboardGoalThemeText = formatThemeList(progressSummary.developmentGoals);
  const snowboardStrengthThemeText = formatThemeList(progressSummary.strengths);

  return (
    <div className="max-w-6xl mx-auto p-4">
      <ProfileHeader />

      {/* Performance Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
        <div className="bg-[#E3F2FD] text-[#1565C0] p-4 rounded-xl shadow">
          <p className="text-sm font-medium">Total Videos Uploaded</p>
          <h2 className="text-2xl font-bold">{totalVideos}</h2>
        </div>
        <div className="bg-[#E8F5E9] text-[#2E7D32] p-4 rounded-xl shadow">
          <p className="text-sm font-medium">Reviewed</p>
          <h2 className="text-2xl font-bold">{reviewedVideos}</h2>
        </div>
        <div className="bg-[#FFF3E0] text-[#EF6C00] p-4 rounded-xl shadow">
          <p className="text-sm font-medium">In Review / Awaiting Claim</p>
          <h2 className="text-2xl font-bold">{inReviewVideos + awaitingInstructorVideos}</h2>
        </div>
        <div className="bg-[#FDECEC] text-[#C62828] p-4 rounded-xl shadow">
          <p className="text-sm font-medium">Uploads Needing Attention</p>
          <h2 className="text-2xl font-bold">{failedUploads}</h2>
        </div>
      </div>

      <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Sessions</p>
            <h2 className="mt-2 text-xl font-semibold text-slate-950">Bookings and Follow-Up</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Follow your confirmed sessions through upload, feedback, AI coaching notes, and next-session momentum.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate('/dashboard/progress')}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Open Progress Dashboard
            </button>
            <button
              onClick={() => navigate('/dashboard/packages')}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Compare Packages
            </button>
            <button
              onClick={() => navigate('/my-bookings')}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Open My Bookings
            </button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="rounded-2xl bg-sky-50 p-4">
            <p className="text-sm font-medium text-sky-700">Upcoming Bookings</p>
            <p className="mt-2 text-3xl font-bold text-sky-900">{upcomingBookings.length}</p>
          </div>
          <div className="rounded-2xl bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-700">Requests In Flight</p>
            <p className="mt-2 text-3xl font-bold text-amber-900">{bookingRequests.filter((booking) => ['pending', 'accepted'].includes(booking.status)).length}</p>
          </div>
          <div className="rounded-2xl bg-rose-50 p-4">
            <p className="text-sm font-medium text-rose-700">Need Upload / Follow-Up</p>
            <p className="mt-2 text-3xl font-bold text-rose-900">{followUpBookings.length}</p>
          </div>
          <div className="rounded-2xl bg-emerald-50 p-4">
            <p className="text-sm font-medium text-emerald-700">Feedback Ready</p>
            <p className="mt-2 text-3xl font-bold text-emerald-900">{feedbackReadyBookings.length}</p>
          </div>
        </div>

        <div className="mt-5 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-900">Upcoming Sessions</h3>
            {upcomingBookings.length === 0 ? (
              <p className="mt-3 text-sm text-slate-600">No upcoming sessions yet.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {upcomingBookings.slice(0, 4).map((booking) => (
                  <div key={booking.bookingId || booking.id} className="rounded-xl border border-slate-200 bg-white p-3">
                    <p className="text-sm font-semibold text-slate-900">{booking.serviceType || 'Session'}</p>
                    <p className="mt-1 text-sm text-slate-600">{booking.status}</p>
                    <p className="mt-1 text-xs text-slate-500">{booking.startAt || 'Start TBD'} · {booking.timezone}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-900">Post-Session Next Steps</h3>
            {followUpBookings.length === 0 ? (
              <p className="mt-3 text-sm text-slate-600">No completed sessions need follow-up right now.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {followUpBookings.slice(0, 4).map((booking) => (
                  <div key={booking.bookingId || booking.id} className="rounded-xl border border-slate-200 bg-white p-3">
                    <p className="text-sm font-semibold text-slate-900">{booking.serviceType || 'Session'}</p>
                    <p className="mt-1 text-sm text-slate-600">Session completed. Upload video or notes to continue coaching.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        onClick={() => navigate('/upload')}
                        className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white"
                      >
                        Upload Session Video
                      </button>
                      <button
                        onClick={() => navigate('/my-bookings')}
                        className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-900"
                      >
                        Rebook Session
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 mt-6 justify-between items-center">
        <input
          type="text"
          placeholder="🔍 Filter by title..."
          className="w-full md:w-1/2 p-2 border rounded shadow-sm"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/upload')}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            ⬆️ Upload New Video
          </button>
          <button
            onClick={() => alert('Upgrade plan coming soon')}
            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
          >
            🌟 Upgrade Plan
          </button>
        </div>
      </div>

      <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Progress</p>
            <h2 className="mt-2 text-xl font-semibold text-slate-950">Coaching Trends</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              A simple view of the themes, corrections, and drills your recent reviews keep returning to.
            </p>
          </div>
          <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Based on your last{' '}
            <span className="font-semibold text-slate-900">{progressSummary.recentReviewCount}</span>{' '}
            reviewed {progressSummary.recentReviewCount === 1 ? 'video' : 'videos'}
          </div>
        </div>

        {progressSummary.reviewedCount === 0 ? (
          <p className="mt-4 text-sm text-slate-500">
            Your coaching trends will appear here once you have reviewed videos.
          </p>
        ) : !progressSummary.hasProgressData ? (
          <p className="mt-4 text-sm text-slate-500">
            Your reviewed videos are available, but there is not enough structured trend data yet to show a useful summary.
          </p>
        ) : (
          <div className="mt-5 grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)_minmax(0,1fr)]">
            <div className="rounded-2xl bg-indigo-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Reviewed Videos</p>
              <p className="mt-2 text-3xl font-bold text-indigo-900">{progressSummary.reviewedCount}</p>
              <p className="mt-2 text-sm text-indigo-900">
                Coaches are starting to build continuity across your reviewed sessions.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Current Profile</h3>
              <p className="mt-1 text-sm text-slate-500">
                {isSnowboardProgress
                  ? 'A compact snowboard summary of the movement themes your recent coaching is building toward.'
                  : 'A compact summary of what your recent coaching is building toward.'}
              </p>
              {progressSummary.currentLevelSummary ? (
                <p className="mt-4 text-sm text-slate-700">{progressSummary.currentLevelSummary}</p>
              ) : (
                <p className="mt-4 text-sm text-slate-500">
                  This summary will become clearer as more structured reviews are completed.
                </p>
              )}
              {progressSummary.lessonReinforcementText ? (
                <p className="mt-3 text-sm text-indigo-700">{progressSummary.lessonReinforcementText}</p>
              ) : null}
              {progressSummary.terrainComfort.bestKnown && (
                <p className="mt-3 text-sm text-slate-700">
                  Terrain comfort: <span className="font-medium capitalize">{progressSummary.terrainComfort.bestKnown}</span>
                </p>
              )}
              {progressSummary.terrainComfort.workingOn && (
                <p className="mt-2 text-sm text-slate-700">
                  Working on: <span className="font-medium capitalize">{progressSummary.terrainComfort.workingOn}</span>
                </p>
              )}
              {isSnowboardProgress && snowboardStrengthThemeText && (
                <p className="mt-3 text-sm text-slate-700">
                  Built strengths: <span className="font-medium">{snowboardStrengthThemeText}</span>
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Current Focus</h3>
              <p className="mt-1 text-sm text-slate-500">
                {isSnowboardProgress
                  ? 'Recurring snowboard themes your recent reviews and drills keep reinforcing.'
                  : 'Themes that your recent reviews and drills keep reinforcing.'}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {progressSummary.topFocusAreas.map((item) => (
                  <span
                    key={item.key || item.label}
                    className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700"
                  >
                    {item.label}
                    {item.count > 1 ? ` • ${item.count}` : ''}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Skill Focus</h3>
              <p className="mt-1 text-sm text-slate-500">
                Recurring SAS ski skills your recent reviews are building toward.
              </p>
              {progressSummary.topSkills.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">
                  Skill focus will appear here once your ski reviews contain enough structured signals.
                </p>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  {progressSummary.topSkills.map((item) => (
                    <span
                      key={item.label}
                      className="rounded-full bg-indigo-50 px-3 py-1 text-sm text-indigo-700"
                    >
                      {item.label}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Still Recurring</h3>
              <p className="mt-1 text-sm text-slate-500">
                {isSnowboardProgress
                  ? 'Recurring snowboard movement themes that are still showing up across recent reviews.'
                  : 'Movement issues that are still showing up across recent reviews.'}
              </p>
              {progressSummary.recurringIssues.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">
                  Recurring issues will appear here once your structured reviews contain enough signal.
                </p>
              ) : (
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {progressSummary.recurringIssues.map((item) => (
                    <li key={item.key} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2">
                      <span>{item.label}</span>
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        {item.count}x
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {isSnowboardProgress && snowboardRecurringThemeText && (
                <p className="mt-3 text-sm text-slate-700">
                  Your recent snowboard reviews still keep returning to <span className="font-medium">{snowboardRecurringThemeText}</span>.
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Development Goals</h3>
              <p className="mt-1 text-sm text-slate-500">
                {isSnowboardProgress
                  ? 'Recurring snowboard correction themes and next training priorities that keep coming up.'
                  : 'Correction themes and next training priorities that keep coming up.'}
              </p>
              {progressSummary.developmentGoals.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">
                  Development goals will appear here once coaches add more structured correction cues.
                </p>
              ) : (
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {progressSummary.developmentGoals.map((item) => (
                    <li key={item.key} className="rounded-xl bg-slate-50 px-3 py-2">
                      {item.label}
                    </li>
                  ))}
                </ul>
              )}
              {isSnowboardProgress && snowboardGoalThemeText && (
                <p className="mt-3 text-sm text-slate-700">
                  Next snowboard training focus: <span className="font-medium">{snowboardGoalThemeText}</span>.
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">What&apos;s Improving</h3>
              <p className="mt-1 text-sm text-slate-500">
                Simple trend signals based on how themes show up across older versus newer reviews.
              </p>
              {Object.keys(progressSummary.trendSignals || {}).length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">
                  Trend signals will appear once there are enough reviewed sessions to compare.
                </p>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  {Object.entries(progressSummary.trendSignals).map(([key, value]) => (
                    <span
                      key={key}
                      className="rounded-full bg-emerald-50 px-3 py-1 text-sm text-emerald-700"
                    >
                      {key.replace(/_/g, ' ')}: {value}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Suggested Practice Drills</h3>
              <p className="mt-1 text-sm text-slate-500">
                Drills your reviews are returning to most often.
              </p>
              {progressSummary.topDrills.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">
                  Drill recommendations will appear here once coaches start attaching them to reviews.
                </p>
              ) : (
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {progressSummary.topDrills.map((item) => (
                    <li key={item.label} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2">
                      <span>{item.label}</span>
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        {item.count}x
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 p-4 lg:col-span-2">
              <h3 className="text-sm font-semibold text-slate-900">Next Lesson Plan</h3>
              <p className="mt-1 text-sm text-slate-500">
                {progressionDisplay.introText}
              </p>
              {!progressionDisplay.hasRecommendation ? (
                <p className="mt-4 text-sm text-slate-500">
                  {progressionDisplay.emptyStateText}
                </p>
              ) : (
                <div className="mt-4 space-y-4">
                  <div className="grid gap-3 md:grid-cols-4">
                    {progressionDisplay.cards.map((card) => (
                      <div key={card.label} className="rounded-xl bg-slate-50 px-3 py-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{card.label}</p>
                        <p className="mt-2 text-sm font-medium text-slate-900">{card.value}</p>
                      </div>
                    ))}
                  </div>

                  {progressionDisplay.showReason && (
                    <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                        {progressionDisplay.reasonTitle}
                      </p>
                      <p className="mt-2 text-sm text-indigo-900">
                        {progressionDisplay.reasonText}
                      </p>
                    </div>
                  )}

                  {progressionDisplay.hasLessonFlow ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-900">{progressionDisplay.lessonFlowTitle}</p>
                      <div className="mt-4 grid gap-3 md:grid-cols-5">
                        {progressionDisplay.lessonFlowStages.map((stage) => (
                          <div key={stage.key} className="rounded-xl bg-white p-3 shadow-sm">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              {stage.title}
                            </p>
                            {stage.hasDrills ? (
                              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                                {stage.drillNames.map((drillName) => (
                                  <li key={drillName}>{drillName}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="mt-2 text-sm text-slate-500">{stage.emptyText}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">
                      {progressionDisplay.lessonFlowEmptyText}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Lessons</p>
            <h2 className="mt-2 text-xl font-semibold text-slate-950">My Lessons</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Open lessons as their own coaching surface, whether they were created from a review or planned directly for you by the school.
            </p>
            {lessonClaimMessage ? (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                {lessonClaimMessage}
              </div>
            ) : null}
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <span className="font-semibold text-slate-900">{lessonInstances.length}</span> total lessons
              </div>
              <div className="rounded-2xl bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
                <span className="font-semibold">{plannedLessons.length}</span> planned
              </div>
              <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                <span className="font-semibold">{completedLessons.length}</span> completed
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate(buildStudentLessonPath())}
            className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800"
          >
            View My Lessons
          </button>
        </div>
      </section>

      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Progression</p>
            <h2 className="mt-2 text-xl font-semibold text-slate-950">My Skills</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Open your sport roadmap to see your current level, active track, next skill, and the branches you can grow into. Tap a sport below to jump straight into that tree.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {skillCardSummaries.map((item) => (
                <button
                  key={item.sport}
                  type="button"
                  onClick={() => navigate(buildMySkillsPath({ sport: item.sport }))}
                  className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700 hover:bg-slate-200"
                >
                  {item.sport}
                </button>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              {skillCardSummaries.map((item) => (
                <button
                  key={`${item.sport}-summary`}
                  type="button"
                  onClick={() => navigate(buildMySkillsPath({ sport: item.sport }))}
                  className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm text-slate-700 hover:border-slate-300 hover:bg-slate-100"
                >
                  <span className="font-semibold">{item.sport}</span> {'\u2014'} {item.levelLabel} {'\u2014'} Working on {item.workingOn}
                </button>
              ))}
            </div>
            {(progressSummary.currentSkill || progressSummary.nextSkill) && (
              <div className="mt-4 flex flex-wrap gap-3">
                {progressSummary.currentSkill && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        buildMySkillsPath({
                          sport: progressSummary.dominantSport || skillCardSummaries[0]?.sport || '',
                          skill: progressSummary.currentSkill.skillId,
                        })
                      )
                    }
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                  >
                    View current skill
                  </button>
                )}
                {progressSummary.nextSkill && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        buildMySkillsPath({
                          sport: progressSummary.dominantSport || skillCardSummaries[0]?.sport || '',
                          skill: progressSummary.nextSkill.skillId,
                        })
                      )
                    }
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                  >
                    View next skill
                  </button>
                )}
              </div>
            )}
          </div>
          <button
            onClick={() =>
              navigate(
                buildMySkillsPath({
                  sport: progressSummary.dominantSport || skillCardSummaries[0]?.sport || '',
                })
              )
            }
            className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800"
          >
            View My Skills
          </button>
        </div>
      </section>

      <h2 className="text-xl font-semibold mt-8 mb-4">🎞️ Your Uploaded Videos</h2>

      {loading ? (
        <p>Loading...</p>
      ) : filteredVideos.length === 0 ? (
        <p>No videos uploaded yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredVideos.map((video) => (
            <div
              key={video.id}
              ref={(el) => {
                cardRefs.current[video.id] = el;
              }}
              className={`border rounded-xl p-4 shadow-md ${
                focusedVideoId === video.id ? 'ring-2 ring-indigo-500 ring-offset-2' : ''
              }`}
            >
              {(() => {
                const videoState = getStudentVideoState(video);

                return (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-lg">{getVideoTitle(video)}</p>
                        <p className="text-sm text-gray-600 mb-2">Sport: {video.sport || 'N/A'}</p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${videoState.tone}`}>
                        {videoState.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{videoState.description}</p>
                  </>
                );
              })()}
              {getVideoUrl(video) ? (
                <video
                  ref={(el) => (videoRefs.current[video.id] = el)}
                  src={getVideoUrl(video)}
                  controls
                  className="w-full rounded"
                />
              ) : (
                <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                  Video preview unavailable because this upload did not complete.
                </div>
              )}

              {isReviewedVideo(video) ? (
                <div className="mt-4">
                  <h3 className="font-semibold text-blue-700 mb-2">✅ Feedback Received:</h3>
                  <div className="mb-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/reviews/${video.id}`)}
                      className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700"
                    >
                      View Full Review
                    </button>
                  </div>
                  {video.reviewAudioURL && (
                    <div className="mb-4 rounded-lg bg-slate-50 p-3">
                      <p className="text-sm font-medium text-slate-700 mb-2">Instructor Audio Note</p>
                      <audio controls src={video.reviewAudioURL} className="w-full">
                        Your browser does not support audio playback.
                      </audio>
                    </div>
                  )}
                  {feedbackMap[video.id]?.length > 0 ? (
                    feedbackMap[video.id].map((fb, idx) => (
                      <div
                        key={idx}
                        className="mb-2 border-l-4 border-blue-500 pl-3"
                      >
                        <p>
                          <button
                            onClick={() => {
                              const vid = videoRefs.current[video.id];
                              if (vid) {
                                vid.currentTime = fb.timestamp;
                                vid.play();
                              }
                            }}
                            className="text-indigo-600 font-semibold hover:underline mr-2"
                          >
                            {fb.timestamp}s
                          </button>
                          {fb.content}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No feedback available.</p>
                  )}
                </div>
              ) : isUploadFailed(video) ? (
                <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  This upload failed before the video was fully saved. Upload it again when you are ready.
                </div>
              ) : isInReviewVideo(video) ? (
                <p className="text-gray-500 mt-2">🛠️ An instructor has claimed this video and is preparing feedback.</p>
              ) : (
                <p className="text-gray-500 mt-2">⏳ Waiting for an instructor to claim this video.</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default StudentDashboard;
